import { zodResolver } from "@hookform/resolvers/zod";
import dayjs from "dayjs";
import capitalize from "lodash/capitalize";
import { useState } from "react";
import { Controller, type FieldErrors, useForm } from "react-hook-form";
import { z } from "zod";

import { useGetAppsByIds } from "@web-app/api";
import {
  Button,
  CurrencyInput,
  DateTimeTz,
  ErrorMessage,
  Field,
  Header,
  Label,
  SelectInput,
  Tooltip,
  useAlert,
} from "@web-app/components";
import {
  Order,
  OrderDeliveryType,
  SystemApp,
  TimezoneType,
  UberResponse,
} from "@web-app/types";
import http from "@web-app/utils/http";

/**
 * Selects are cleared to "" whenever the delivery type changes, so the fields
 * stay plain strings and the allowed set is enforced on submit instead of
 * through z.enum — an enum type would reject the intermediate empty state.
 */
const oneOf = (values: string[], message: string) =>
  z.string().refine((value) => values.includes(value), message);

const uberFormSchema = z
  .object({
    deliverableAction: oneOf(
      ["deliverable_action_meet_at_door", "deliverable_action_leave_at_door"],
      "Please select a deliverable action",
    ),
    // Only shown (and required) for normal deliveries; validated below
    deliveryMode: z.string(),
    deliveryType: oneOf(
      ["normal", "catering"],
      "Please select a delivery service",
    ),
    itemSize: oneOf(
      ["small", "medium", "large", "xlarge"],
      "Please select an item size",
    ),
    // Numeric value as string (e.g., "5.50")
    tip: z
      .string()
      .trim()
      .regex(/^\d*\.?\d{0,2}$/, "Please enter a valid amount (e.g., 5.50)")
      .refine(
        (value) => !value || parseFloat(value) >= 0,
        "Tip amount cannot be negative",
      ),
    undeliverableAction: oneOf(
      ["return", "discard", "leave_at_door"],
      "Please select an undeliverable action",
    ),
  })
  .superRefine((data, ctx) => {
    if (
      data.deliveryType === "normal" &&
      !["normal", "asap"].includes(data.deliveryMode)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please select a delivery mode",
        path: ["deliveryMode"],
      });
    }
  });

type UberFormData = z.infer<typeof uberFormSchema>;

const convertTipToNumber = (tipValue: string): number => {
  const numericValue = parseFloat(tipValue);
  return isNaN(numericValue) ? 0 : numericValue;
};

const preValidation = (orders: Order[]) => {
  const errors: string[] = [];
  if (orders.length === 1) {
    const order = orders[0];
    if (!order.details.value) {
      errors.push("Order value is required");
    }
    if (!order.details.itemsCount) {
      errors.push("Order items count is required");
    }
    const windowInMinutes = dayjs(order.scheduledEndDateTime).diff(
      dayjs(order.scheduledStartDateTime),
      "minute",
    );

    if (windowInMinutes < 30) {
      errors.push("More than 30 minute delivery window required");
    }

    if (dayjs(order.scheduledStartDateTime).isBefore(dayjs())) {
      errors.push(
        "Delivery schedule start time must be in the future. At least 30 minutes from now is recommended.",
      );
    }
  } else {
    const pickupOrder = orders.find(
      (o) => o.type === OrderDeliveryType.ROUTE_OPT_PICKUP,
    );
    const dropoffOrder = orders.find(
      (o) => o.type === OrderDeliveryType.ROUTE_OPT_DROPOFF,
    );

    if (!pickupOrder || !dropoffOrder) {
      errors.push("Both pickup and drop-off orders are required");
    }

    if (!pickupOrder?.details.value) {
      errors.push("Pickup order value is required");
    }
    if (!pickupOrder?.details.itemsCount) {
      errors.push("Pickup order items count is required");
    }
    const windowInMinutes = dayjs(dropoffOrder?.scheduledEndDateTime).diff(
      dayjs(pickupOrder?.scheduledStartDateTime),
      "minute",
    );
    if (windowInMinutes < 30) {
      errors.push("More than 30 minute delivery window required");
    }
    if (dayjs(pickupOrder?.scheduledStartDateTime).isBefore(dayjs())) {
      errors.push(
        "Pickup order schedule start time must be in the future. At least 30 minutes from now is recommended.",
      );
    }
  }
  return errors;
};

const getInitialTip = (orders: Order[]) => {
  if (orders.length > 0) {
    return orders[0].details.tip?.toFixed(2) || "0.00";
  }
  return "0.00";
};

export function OrderUberDetails({
  entity,
  entityId,
  timezone,
  onClose,
  onCreateDelivery,
  relatedOrders,
}: {
  entity: "order" | "route";
  entityId: string;
  onClose: () => void;
  onCreateDelivery?: (deliveryId: string) => void;
  relatedOrders: Order[];
  timezone: string;
}) {
  const { showAlert, showAlertAsync } = useAlert();
  let isUberAppActive = false;
  let isUberCateringAppActive = false;

  const { data: apps } = useGetAppsByIds([]);
  if (apps?.length) {
    isUberAppActive = !!apps.find(
      (a) => a.systemApp === SystemApp.UBER && a.isActive,
    );
    isUberCateringAppActive = !!apps.find(
      (a) => a.systemApp === SystemApp.UBER_CATERING && a.isActive,
    );
  }

  const [isLoading, setIsLoading] = useState(false);
  const [uberQuotation, setUberQuotation] = useState<UberResponse>({
    courier: {
      name: "",
      phone_number: "",
      public_phone_info: {
        formatted_phone_number: "",
      },
    },
    deliverable_action: "",
    dropoff_deadline: "",
    dropoff_eta: "",
    expires: "",
    fee: 0,
    id: "",
    pickup_eta: new Date(),
    status: "",
    tracking_url: "",
    undeliverable_action: "",
    undeliverable_reason: "",
  });

  const [isQuotationFetched, setIsQuotationFetched] = useState(false);

  const preValidationErr = preValidation(relatedOrders);

  const { control, handleSubmit, watch, setValue, getValues } =
    useForm<UberFormData>({
      defaultValues: {
        deliverableAction: "deliverable_action_meet_at_door",
        deliveryMode: "normal",
        deliveryType: "normal",
        itemSize: "medium",
        tip: getInitialTip(relatedOrders),
        undeliverableAction: "return",
      },
      mode: "onChange",
      resolver: zodResolver(uberFormSchema),
    });

  const deliveryType = watch("deliveryType");

  const getDeliverableActionOptions = () => {
    const options = [
      {
        label: "Meet at door",
        value: "deliverable_action_meet_at_door",
      },
      {
        label: "Leave at door",
        value: "deliverable_action_leave_at_door",
      },
    ];

    return options;
  };

  const getUndeliverableActionOptions = () => {
    const options = [
      {
        label: "Return",
        value: "return",
      },
      {
        label: "Discard",
        value: "discard",
      },
      {
        label: "Leave at door",
        value: "leave_at_door",
      },
    ];

    return options;
  };

  const onChangeDeliveryType = (value: string) => {
    setValue("deliverableAction", "");
    setValue("undeliverableAction", "");
    setValue("deliveryMode", "normal");
    setValue("deliveryType", value);
    setValue("itemSize", "");
  };

  const onChangeDeliverableAction = (value: string) => {
    setValue("deliverableAction", value);
  };

  const getUberQuotation = async (formData: UberFormData) => {
    setIsLoading(true);
    const data = await showAlertAsync(
      http.post(`/integrations/uber/quote?id=${entityId}&entity=${entity}`, {
        deliveryType: formData.deliveryType,
      }),
      {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        error: (err: any) => {
          onClose();

          const errList = Object.values(err.response?.data?.metadata ?? {}).map(
            (v) => capitalize(v as string),
          );

          const errMsg = `${err.response?.data?.message} ${errList.join("\n")}`;

          return {
            message: errMsg.trim() || "Failed to fetch uber quotation",
            title: "Error",
            type: "error",
          };
        },
        loading: {
          message: "Fetching Uber quotation...",
          title: "Fetching",
          type: "default",
        },
        success: {
          message: "Uber quotation fetched",
          title: "Success",
          type: "success",
        },
      },
    ).then((response) => response.data);

    data.pickup_eta = dayjs().add(data.pickup_duration, "minute").toDate();
    setUberQuotation(data);
    setIsQuotationFetched(true);
    setIsLoading(false);
  };

  const onInvalidForm = (errors: FieldErrors<UberFormData>) => {
    const messages = Object.values(errors)
      .map((error) => error?.message)
      .filter((message): message is string => !!message);

    showAlert({
      title: messages.join(". ") || "Please fill in all required fields",
      type: "error",
    });
  };

  const handleAcceptQuote = async () => {
    setIsLoading(true);
    const formData = watch();
    await showAlertAsync(
      http.post(
        `/integrations/uber/deliveries?id=${entityId}&quoteId=${uberQuotation.id}&entity=${entity}`,
        {
          deliverableAction: formData.deliverableAction,
          deliveryMode: formData.deliveryMode,
          deliveryType: formData.deliveryType,
          itemSize: formData.itemSize,
          tip: convertTipToNumber(formData.tip),
          undeliverableAction: formData.undeliverableAction,
        },
      ),
      {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        error: (err: any) => {
          onClose();
          const errList = Object.values(err.response?.data?.metadata ?? {}).map(
            (v) => capitalize(v as string),
          );
          const errMsg = `${err.response?.data?.message} ${errList.join("\n")}`;
          return {
            message: errMsg.trim() || "Failed to create delivery",
            title: "Error",
            type: "error",
          };
        },
        loading: {
          message: "Sending order to uber...",
          title: "Sending",
          type: "default",
        },
        success: (resp) => {
          onCreateDelivery?.(resp.data.id);
          return {
            message: "Order sent to uber",
            title: "Success",
            type: "success",
          };
        },
      },
    );
    setIsLoading(false);
    onClose();
  };

  const dropoffEtaTime = new Date(uberQuotation.dropoff_eta);
  const dropoffDeadline = new Date(uberQuotation.dropoff_deadline);
  const duration = dayjs(dropoffEtaTime).diff(
    dayjs(uberQuotation.pickup_eta),
    "minute",
  );

  const getUberOptions = () => {
    const options = [];

    if (isUberAppActive) {
      options.push({
        label: "Normal",
        value: "normal",
      });
    }

    if (isUberCateringAppActive) {
      options.push({
        label: "Catering",
        value: "catering",
      });
    }

    return options;
  };

  return (
    <div className="absolute bg-white h-full w-full z-30 flex flex-col">
      {/* Sticky Header */}
      <Header
        title="Send Order to Uber"
        leftActions={
          <Button
            icon="fasChevronLeft"
            onClick={onClose}
            plain
            className="!p-0 hover:!bg-white"
          />
        }
      />

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {preValidationErr?.length > 0 && (
          <div className="flex flex-col">
            <h3 className="text-md font-semibold text-gray-700 mb-2">
              Please address the following issues before proceeding:
            </h3>
            {preValidationErr.map((msg, idx) => (
              <ErrorMessage key={idx} message={`${idx + 1}. ${msg}`} />
            ))}
          </div>
        )}

        {isQuotationFetched && preValidationErr?.length === 0 && (
          <div className="w-full bg-white rounded-md space-y-2">
            <div className="grid grid-cols-3 gap-4 pb-2">
              <div className="col-span-1 flex items-center">
                <Label className="text-sm font-medium text-gray-700">
                  Cost
                </Label>
              </div>
              <div className="col-span-2">
                <div className="text-sm font-medium text-gray-700">
                  {`$${(uberQuotation.fee / 100).toFixed(2)}`}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 pb-2">
              <div className="col-span-1 flex items-center">
                <Label className="text-sm font-medium text-gray-700">Tip</Label>
              </div>
              <div className="col-span-2">
                <div className="text-sm font-medium text-gray-700">
                  {`$${getValues("tip") || "0.00"}`}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 pb-2">
              <div className="col-span-1 flex items-center">
                <Label className="text-sm font-medium text-gray-700">
                  Duration
                </Label>
              </div>
              <div className="col-span-2">
                <div className="text-sm font-medium text-gray-700">
                  {`${duration} minutes`}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 pb-2">
              <div className="col-span-1 flex items-center">
                <Label className="text-sm font-medium text-gray-700">
                  Driver Arrival ETA
                </Label>
              </div>
              <div className="col-span-2">
                <div className="text-sm font-medium text-gray-700">
                  <DateTimeTz
                    start={new Date(uberQuotation.pickup_eta)}
                    timezone={timezone as TimezoneType}
                    presentation="default"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 pb-2">
              <div className="col-span-1 flex items-center">
                <Label className="text-sm font-medium text-gray-700">
                  Driver Drop-off ETA
                </Label>
              </div>
              <div className="col-span-2">
                <div className="text-sm font-medium text-gray-700">
                  <DateTimeTz
                    start={new Date(dropoffEtaTime)}
                    timezone={timezone as TimezoneType}
                    presentation="default"
                  />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-1 flex items-center">
                <Label className="text-sm font-medium text-gray-700">
                  Driver Drop-off Deadline
                </Label>
              </div>
              <div className="col-span-2">
                <div className="text-sm font-medium text-gray-700">
                  <DateTimeTz
                    start={new Date(dropoffDeadline)}
                    timezone={timezone as TimezoneType}
                    presentation="default"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {!isQuotationFetched && preValidationErr?.length === 0 && (
          <div className="w-full bg-white rounded-md">
            <div className="flex flex-col gap-2">
              <Field className="w-full">
                <Label>Delivery Type</Label>
                <Controller
                  name="deliveryType"
                  control={control}
                  rules={{
                    required: "Delivery type is required",
                  }}
                  render={({ field }) => (
                    <SelectInput
                      options={getUberOptions()}
                      name="delivery_type"
                      selected={field.value}
                      onChange={(value: string) => {
                        field.onChange(value);
                        onChangeDeliveryType(value);
                      }}
                      required
                    />
                  )}
                />
              </Field>

              <Field className="w-full">
                <Label>Deliverable Action</Label>
                <Controller
                  name="deliverableAction"
                  control={control}
                  rules={{
                    required: "Deliverable action is required",
                  }}
                  render={({ field }) => (
                    <SelectInput
                      options={getDeliverableActionOptions()}
                      name="deliverable_action"
                      selected={field.value}
                      onChange={(value: string) => {
                        field.onChange(value);
                        onChangeDeliverableAction(value);
                      }}
                      required
                    />
                  )}
                />
              </Field>

              <Field className="w-full">
                <Label>Undeliverable Action</Label>
                <Controller
                  name="undeliverableAction"
                  control={control}
                  rules={{
                    required: "Undeliverable action is required",
                  }}
                  render={({ field }) => (
                    <SelectInput
                      options={getUndeliverableActionOptions()}
                      name="undeliverable_action"
                      selected={field.value}
                      onChange={(value: string) => {
                        field.onChange(value);
                      }}
                      required
                    />
                  )}
                />
              </Field>

              {deliveryType === "normal" && (
                <Field className="w-full">
                  <Label>Delivery Mode</Label>
                  <Controller
                    name="deliveryMode"
                    control={control}
                    rules={{
                      required: "Delivery mode is required",
                    }}
                    render={({ field }) => (
                      <SelectInput
                        options={[
                          {
                            label: "Normal",
                            value: "normal",
                          },
                          {
                            label: "Asap",
                            value: "asap",
                          },
                        ]}
                        name="delivery_mode"
                        selected={field.value}
                        onChange={(value: string) => {
                          field.onChange(value);
                          setValue("deliveryMode", value);
                        }}
                        required
                      />
                    )}
                  />
                </Field>
              )}

              <Field className="w-full">
                <Label>Item Size</Label>
                <Controller
                  name="itemSize"
                  control={control}
                  rules={{
                    required: "Item size is required",
                  }}
                  render={({ field }) => (
                    <SelectInput
                      options={[
                        {
                          label: "Small eg. water bottle",
                          value: "small",
                        },
                        {
                          label: "Medium eg. retail bag",
                          value: "medium",
                        },
                        {
                          label: "Large eg. computer monitor",
                          value: "large",
                        },
                        {
                          label: "X-Large eg. 42 inch flat screen TV",
                          value: "xlarge",
                        },
                      ]}
                      name="item_size"
                      selected={field.value}
                      onChange={(value: string) => {
                        field.onChange(value);
                        setValue("itemSize", value);
                      }}
                      required
                    />
                  )}
                />
              </Field>

              <Field className="w-full">
                <Label>Add Tip</Label>
                <Controller
                  name="tip"
                  control={control}
                  rules={{
                    pattern: {
                      message: "Please enter a valid amount (e.g., 5.50)",
                      value: /^\d*\.?\d{0,2}$/,
                    },
                    validate: (value) => {
                      if (value && isNaN(parseFloat(value))) {
                        return "Please enter a valid number";
                      }
                      if (value && parseFloat(value) < 0) {
                        return "Tip amount cannot be negative";
                      }
                      return true;
                    },
                  }}
                  render={({ field }) => (
                    <CurrencyInput
                      value={field.value}
                      onChange={field.onChange}
                      selectedCurrency="USD"
                      onCurrencyChange={() => {}}
                      currencyOptions={[{ label: "USD", value: "USD" }]}
                    />
                  )}
                />
              </Field>
            </div>
          </div>
        )}
      </div>

      {/* Sticky Footer */}
      <div className="border-t border-gray-200 p-4 bg-white">
        <div className="flex gap-2">
          {isQuotationFetched && (
            <Tooltip text="Accept quote to send order to Uber">
              <Button
                type="button"
                isLoading={isLoading}
                onClick={() => handleAcceptQuote()}
              >
                Accept Quote
              </Button>
            </Tooltip>
          )}
          {!isQuotationFetched && (
            <Tooltip text="Request quote to see the estimated cost">
              <Button
                type="button"
                isLoading={isLoading}
                onClick={handleSubmit(getUberQuotation, onInvalidForm)}
                disabled={preValidationErr?.length > 0}
              >
                Request Quote
              </Button>
            </Tooltip>
          )}
          <Button type="button" plain onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}
